import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { FiCalendar, FiClock } from 'react-icons/fi';

const HolidayDisplay = () => {
    const [holiday, setHoliday] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isFlipped, setIsFlipped] = useState(false);
    const [colorIndex, setColorIndex] = useState(0);
    const flipCountRef = useRef(0);
    const intervalRef = useRef(null);

    const softColors = {
        increase: {
            primary: '#10b981',
            light: '#d1fae5',
            gradient: 'linear-gradient(to top, #10b981, #34d399)',
            shadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
        },
        decrease: {
            primary: '#ef4444',
            light: '#fee2e2',
            gradient: 'linear-gradient(to top, #ef4444, #f87171)',
            shadow: '0 2px 8px rgba(239, 68, 68, 0.3)'
        },
        neutral: {
            primary: '#6b7280',
            light: '#f3f4f6',
            gradient: 'linear-gradient(to top, #6b7280, #9ca3af)',
            shadow: '0 2px 8px rgba(107, 114, 128, 0.3)'
        },
        today: {
            primary: '#3b82f6',
            light: '#dbeafe',
            gradient: 'linear-gradient(to top, #3b82f6, #60a5fa)',
            shadow: '0 2px 12px rgba(59, 130, 246, 0.4)'
        },
        pastel: ['#a78bfa', '#f472b6', '#38bdf8', '#34d399', '#fbbf24'],
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
    };

    // Create soft color variations using the pastel palette
    const colorVariations = softColors.pastel.map(color => ({
        bg: `${color}15`, // Very light background (15% opacity)
        text: color,
        border: `${color}30` // Light border (30% opacity)
    }));

    // Add the main colors from CustomerTrendGraph
    const additionalColors = [
        { bg: '#d1fae520', text: '#10b981', border: '#10b98130' }, // increase
        { bg: '#dbeafe20', text: '#3b82f6', border: '#3b82f630' }, // today
        { bg: '#fef3c720', text: '#f59e0b', border: '#f59e0b30' }, // amber variant
    ];

    const allColors = [...colorVariations, ...additionalColors];

    const getDefaultColor = () => ({
        bg: '#f8fafc',
        text: '#6b7280',
        border: '#e2e8f0'
    });

    const safeHoliday = () => {
        if (!holiday) return null;
        return {
            ...holiday,
            color: holiday.color || getDefaultColor(),
            daysUntil: holiday.daysUntil || 0,
            name: holiday.name || 'Holiday',
            date: holiday.date || ''
        };
    };

    useEffect(() => {
        const fetchHolidays = async () => {
            try {
                setLoading(true);
                const currentYear = new Date().getFullYear();
                const response = await fetch(
                    `https://date.nager.at/api/v3/PublicHolidays/${currentYear}/SG`
                );

                if (!response.ok) {
                    setHoliday(null);
                    setLoading(false);
                    return;
                }

                const holidays = await response.json();
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                const nextHoliday = holidays.find(h => {
                    const holidayDate = new Date(h.date + 'T00:00:00+08:00');
                    return holidayDate >= today;
                });

                if (nextHoliday) {
                    const holidayDate = new Date(nextHoliday.date + 'T00:00:00+08:00');
                    const diffDays = Math.ceil((holidayDate - today) / (86400000));

                    setHoliday({
                        ...nextHoliday,
                        daysUntil: diffDays,
                        isToday: diffDays === 0,
                        isTomorrow: diffDays === 1
                    });
                } else {
                    setHoliday(null);
                }
            } catch (err) {
                console.error('Holiday fetch error:', err);
                setHoliday(null);
            } finally {
                setLoading(false);
            }
        };

        fetchHolidays();
    }, []);

    const handleFlip = () => {
        flipCountRef.current += 1;
        setIsFlipped(prev => !prev);
        setColorIndex((prev) => (prev + 1) % allColors.length);
    };

    useEffect(() => {
        if (!holiday) return;

        intervalRef.current = setInterval(() => {
            handleFlip();
        }, 7000);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [holiday]);

    const safeHolidayData = safeHoliday();
    const colors = safeHolidayData ? allColors[colorIndex] : getDefaultColor();

    const cardHeight = '80px';

    if (!loading && !safeHolidayData) return null;

    const formatDate = (dateString) => {
        if (!dateString) return 'Soon';
        const date = new Date(dateString + 'T00:00:00+08:00');
        return date.toLocaleDateString('en-SG', {
            weekday: 'short',
            day: 'numeric',
            month: 'short'
        });
    };

    const getStatusText = (daysUntil = 0) => {
        if (daysUntil === 0) return 'Today';
        if (daysUntil === 1) return 'Tomorrow';
        if (daysUntil <= 7) return 'This Week';
        return 'Upcoming';
    };

    // Get appropriate status color based on days until
    const getStatusColor = (daysUntil) => {
        if (daysUntil === 0) return softColors.today.primary;
        if (daysUntil === 1) return softColors.increase.primary;
        if (daysUntil <= 7) return '#f59e0b'; // Amber
        return softColors.neutral.primary;
    };

    return (
        <div
            key={`card-${colorIndex}`}
            style={{
                height: cardHeight,
                width: '160px',
                perspective: '1000px',
                cursor: 'pointer'
            }}
            onClick={handleFlip}
        >
            <motion.div
                key="flip-container"
                style={{
                    position: 'relative',
                    width: '100%',
                    height: cardHeight,
                    transformStyle: 'preserve-3d',
                    transition: 'transform 0.6s'
                }}
                animate={{
                    rotateY: isFlipped ? 180 : 0
                }}
            >
                {/* Front Side */}
                <motion.div
                    style={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        backfaceVisibility: 'hidden',
                        background: colors.bg,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '8px',
                        padding: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                    }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                >
                    {/* Animated Calendar Icon */}
                    <motion.div
                        style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            background: `linear-gradient(135deg, ${colors.text}20, ${colors.border})`,
                            color: colors.text,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative'
                        }}
                        animate={{
                            rotate: [0, 5, 0, -5, 0],
                            scale: [1, 1.05, 1]
                        }}
                        transition={{
                            duration: 4,
                            repeat: Infinity,
                            ease: "easeInOut"
                        }}
                    >
                        <FiCalendar size={14} />

                        {/* Subtle pulse effect */}
                        <motion.div
                            style={{
                                position: 'absolute',
                                width: '36px',
                                height: '36px',
                                borderRadius: '50%',
                                border: `1px solid ${colors.text}30`,
                                opacity: 0.3
                            }}
                            animate={{
                                scale: [1, 1.2, 1],
                                opacity: [0.3, 0, 0.3]
                            }}
                            transition={{
                                duration: 3,
                                repeat: Infinity,
                                ease: "easeInOut"
                            }}
                        />
                    </motion.div>

                    <div style={{ flexGrow: 1 }}>
                        {loading ? (
                            <div style={{
                                height: '12px',
                                width: '90%',
                                background: `linear-gradient(90deg, ${colors.border} 25%, ${colors.text}20 50%, ${colors.border} 75%)`,
                                borderRadius: '4px',
                                backgroundSize: '200% 100%',
                                animation: 'loading 1.5s infinite'
                            }} />
                        ) : (
                            <>
                                <motion.div
                                    style={{
                                        fontSize: '0.8rem',
                                        color: colors.text,
                                        fontWeight: '600',
                                        lineHeight: '1.1',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}
                                    animate={{
                                        opacity: [1, 0.9, 1]
                                    }}
                                    transition={{
                                        duration: 3,
                                        repeat: Infinity
                                    }}
                                >
                                    {safeHolidayData?.name || 'Holiday'}
                                </motion.div>
                                <motion.div
                                    style={{
                                        fontSize: '0.65rem',
                                        color: getStatusColor(safeHolidayData?.daysUntil),
                                        fontWeight: 500,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                    }}
                                    animate={{
                                        x: [0, 1, 0, -1, 0]
                                    }}
                                    transition={{
                                        duration: 6,
                                        repeat: Infinity,
                                        ease: "easeInOut"
                                    }}
                                >
                                    <div style={{
                                        width: '6px',
                                        height: '6px',
                                        borderRadius: '50%',
                                        backgroundColor: getStatusColor(safeHolidayData?.daysUntil)
                                    }} />
                                    {getStatusText(safeHolidayData?.daysUntil)}
                                </motion.div>
                            </>
                        )}
                    </div>
                </motion.div>

                <motion.div
                    style={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        backfaceVisibility: 'hidden',
                        transform: 'rotateY(180deg)',
                        background: colors.bg,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '8px',
                        padding: '12px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '4px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                    }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                >
                    <motion.div
                        style={{
                            color: getStatusColor(safeHolidayData?.daysUntil),
                            fontSize: '1.15rem',
                            fontWeight: 'bold',
                            lineHeight: 1,
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                        }}
                        animate={{
                            scale: [1, 1.05, 1],
                            color: [
                                getStatusColor(safeHolidayData?.daysUntil),
                                `${getStatusColor(safeHolidayData?.daysUntil)}CC`,
                                getStatusColor(safeHolidayData?.daysUntil)
                            ]
                        }}
                        transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: "easeInOut"
                        }}
                    >
                        {safeHolidayData?.daysUntil || 0}
                        <span style={{ fontSize: '0.8rem' }}>
                            {safeHolidayData?.daysUntil === 1 ? 'day' : 'days'} left
                        </span>

                        <motion.div
                            style={{
                                position: 'absolute',
                                top: '-5px',
                                right: '-5px',
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: colors.text,
                                opacity: 0.5
                            }}
                            animate={{
                                y: [0, -4, 0],
                                opacity: [0.5, 0.8, 0.5]
                            }}
                            transition={{
                                duration: 1.5,
                                repeat: Infinity,
                                ease: "easeInOut"
                            }}
                        />
                    </motion.div>

                    <motion.div
                        style={{
                            textAlign: 'center',
                            width: '100%'
                        }}
                        animate={{
                            opacity: [0.8, 1, 0.8]
                        }}
                        transition={{
                            duration: 3,
                            repeat: Infinity
                        }}
                    >
                        <div
                            style={{
                                fontSize: '0.7rem',
                                fontWeight: '600',
                                color: colors.text,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '4px'
                            }}
                        >
                            <FiClock size={10} />
                            {formatDate(safeHolidayData?.date)}
                        </div>
                    </motion.div>
                </motion.div>
            </motion.div>

            <style jsx>{`
                @keyframes loading {
                    0% { background-position: 200% 0; }
                    100% { background-position: -200% 0; }
                }
            `}</style>
        </div>
    );
};

export default HolidayDisplay;